import { useMemo } from "react";
import styles from "./ModelDetails.module.scss";
import { FormattedMessage } from "react-intl";
import { IChatModel } from "redux/actions";
import {
  CuttOffDateIcon,
  Text_Icon,
  Image_Icon,
  AudioIcon,
  VideoIcon,
} from "../icons";
import { ReactNode } from "react";
import { getModalityFromMime } from "utils/functions";
import { WebSearchIcon } from "pages/Workspaces/components/icons/Prompt";
import classNames from "classnames";
import { useSelector } from "redux/hooks";
import StarRating from "./StarRating";
import BoltRating from "./BoltRating";

const modalityIcons: Record<string, ReactNode> = {
  text: <Text_Icon />,
  image: <Image_Icon />,
  audio: <AudioIcon />,
  video: <VideoIcon />,
  "web-search": <WebSearchIcon />,
  "text-to-image":<Image_Icon />
};

interface IProps {
  chatModel: IChatModel;
}

export const ModelDetails = ({ chatModel }: IProps) => {
  const { theme } = useSelector((state) => state.authReducer);
  const supportedModalities =
    chatModel?.attributes?.supported_modalities?.split(",") || [];
  const supportedInputFiles =
    chatModel?.attributes?.supported_input_files || "";
  const modalitiesFromFiles = supportedInputFiles
    .split(",")
    .map((mime) => getModalityFromMime(mime))
    .filter(Boolean);

  const uniqueModalities = Array.from(new Set(modalitiesFromFiles));

  // Memoize the Date object to avoid recalculating on every render
  const cutoffLabel = useMemo(() => {
    const dateString = chatModel.attributes?.cutoff_date;
    if (!dateString) return null;

    let year = 0,
      month = 0;

    if (/^\d{4}-\d{2}$/.test(dateString)) {
      [year, month] = dateString.split("-").map(Number);
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      [year, month] = dateString.split("-").map(Number);
    } else {
      return null;
    }

    const date = new Date(year, month - 1); // Use first of the month
    const shortMonth = date.toLocaleString("en-US", { month: "short" });
    const shortYear = String(year).slice(-2);

    return `${shortMonth} ${shortYear}`;
  }, [chatModel.attributes?.cutoff_date]);

  return (
    <div className="font-inter dark:text-dark">
      <div className="flex overflow-x-auto whitespace-nowrap gap-[24px]">
        {/* Intelligence */}
        <div className={styles.container}>
          <h3
            className={classNames(styles.titles, {
              [styles.light]: theme === "light",
              [styles.dark]: theme === "dark",
            })}
          >
            <FormattedMessage id="gptmodel.detailpoint1" />
          </h3>
          <StarRating
            rating={chatModel?.attributes?.intelligence_rating ?? 0}
            maxStars={5}
            color="#FFD700"
          />
        </div>

        {/* Speed */}
        <div className={styles.container}>
          <h3
            className={classNames(styles.titles, {
              [styles.light]: theme === "light",
              [styles.dark]: theme === "dark",
            })}
          >
            <FormattedMessage id="gptmodel.detailpoint2" />
          </h3>
          <BoltRating
            rating={chatModel?.attributes?.speed_rating ?? 0}
            maxBolts={5}
            color="#16A34A"
            defaultColor="#DCFCE7"
          />
        </div>

        {/* Input */}
        <div className={styles.container}>
          <h3
            className={classNames(styles.titles, {
              [styles.light]: theme === "light",
              [styles.dark]: theme === "dark",
            })}
          >
            <FormattedMessage id="gptmodel.detailpoint3" />
          </h3>
          <div className="flex gap-1 text-[16px] dark:text-dark">
            {uniqueModalities.map((modality, idx) => (
              <span key={idx}>
                {modality
                  ? modalityIcons[modality.trim()] ?? modality.trim()
                  : null}
              </span>
            ))}
          </div>
        </div>

        {/* Output */}
        <div className={styles.container}>
          <h3
            className={classNames(styles.titles, {
              [styles.light]: theme === "light",
              [styles.dark]: theme === "dark",
            })}
          >
            <FormattedMessage id="gptmodel.detailpoint4" />
          </h3>
          <div className="flex gap-1 text-[16px] dark:text-dark">
            {supportedModalities.map((modality, idx) => (
              <span key={idx}>
                {modalityIcons[modality.trim()] ?? modality.trim()}
              </span>
            ))}
          </div>
        </div>

        {/* Updated */}
        <div className={styles.container}>
          <h3
            className={classNames(styles.titles, {
              [styles.light]: theme === "light",
              [styles.dark]: theme === "dark",
            })}
          >
            <FormattedMessage id="gptmodel.detailpoint5" />
          </h3>
          <div className="flex items-center gap-2">
            <span
              className={classNames(styles.cutOffDate, {
                [styles.light]: theme === "light",
                [styles.dark]: theme === "dark",
              })}
            >
              {cutoffLabel ?? "-"}
            </span>
            <span className="flex-shrink-0 leading-[0]">
              {cutoffLabel && <CuttOffDateIcon />}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
